import { useState } from "react";

import { cx } from "@/components/ui";

/**
 * The brand tile: the signed-in business's own logo on their own brand colour (PHP-3875) when both are
 * known and safe to show, else the platform mark on the default coral tile — exactly as before branding
 * existed. `logoUrl`/`colourHex` are expected already resolved and guard-passed (see
 * `@/lib/branding`'s `useEffectiveBranding`): this component's own job is just the last-mile fallback —
 * an asset that fails to load here falls back to the platform default, live, without a reload. Size and
 * corner radius come from `className`.
 */
export function BrandMark({
  className,
  logoUrl,
  colourHex,
}: {
  className?: string;
  /** The business's own logo; omit (or let it fail to load) to show the platform mark instead. */
  logoUrl?: string | null;
  /** The business's own brand colour; omit to use the default tile colour. */
  colourHex?: string | null;
}) {
  // Adjusted during render (the React-recommended way to reset state when a prop changes) rather than
  // in an effect: a newly-saved or newly-switched-to logo deserves its own fresh chance to load.
  const [{ forUrl, broken }, setBrokenState] = useState({ forUrl: logoUrl, broken: false });
  if (forUrl !== logoUrl) {
    setBrokenState({ forUrl: logoUrl, broken: false });
  }
  const branded = Boolean(logoUrl) && !broken;
  return (
    <span
      className={cx("block shrink-0 overflow-hidden", !branded && "bg-brand-500", className)}
      style={branded && colourHex ? { backgroundColor: colourHex } : undefined}
    >
      <img
        src={branded ? (logoUrl as string) : "/brand/mark.svg"}
        alt=""
        className="size-full object-contain p-[22%]"
        onError={() => setBrokenState({ forUrl: logoUrl, broken: true })}
      />
    </span>
  );
}

/** The brand tile and name shown on the auth pages. */
export function Logo() {
  return (
    <span className="flex items-center gap-2 font-semibold tracking-tight text-fg">
      <BrandMark className="size-7 rounded-lg" />
      CortexGrip
    </span>
  );
}
